import React, {useState, useEffect, useContext, useRef}  from 'react';
import { useHistory, useLocation } from "react-router-dom";
import SessionContext from "SessionContext";
import { LoadingDots } from "partials/progress-loader";
import { Button, Modal, Alert, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ConfirmSuccess from 'core/ui/confirm-success';
import isEqual from "lodash.isequal";

export default function ExternalDestinationEditForm() {
    const session = useContext(SessionContext);
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const _id = params.get("am_destination_id");
    const _new = params.get("new");
    const hasRunCompleteDestination = useRef(false);

    const [hasCensusId, setHasCensusId] = useState(false);
    const [disableSave, setDisableSave] = useState(true);
    const [destinationData, setDestinationData] = useState({});
    const [originalDestinationData, setOriginalDestinationData] = useState({});
    const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
    const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const onClickBack = (ev) => {
      ev.stopPropagation();
      history.push("/external-destinations");
    }

    const onClickSave = () => {
      let params = {
        "id": destinationData.id,
        "name": destinationData.name,
        "type": destinationData.type,
        "description": destinationData.description,
        "enabled": destinationData.enabled,
        "external_destination_allowed": destinationData.external_destination_allowed
      }

      session.command("census/update-destination/", params, {}, { defaultErrorHandling: false }).then((res) => {
        setShowUpdateSuccess(true);
        setTimeout(() => {
          setShowUpdateSuccess(false);
          history.push("/external-destinations");
        }, 2000);
      })
      .catch((err) => {
        console.log("Failed to update the destination details", err);
      })
    }

    const setDescription = (ev) => {
      ev.preventDefault();
      let _description = ev.target.value || "";
      setDestinationData({...destinationData, description: _description });
    }

    const setName = (ev) => {
      ev.preventDefault();
      let _name = ev.target.value || "";
      setDestinationData({...destinationData, name: _name });
    }

    const setSelectedCompanyIds = (val) => {
      setDestinationData({...destinationData, external_destination_allowed: val });
    }

    const onClickDelete = () => {
      session.command(`census/delete-destination/${_id}`,
        {},
        {defaultErrorHandling: false}
      )
        .then(() => {
          setShowDeleteModal(false);
          setShowDeleteSuccess(true);
          setTimeout(() => {
            setShowDeleteSuccess(false);
            history.push("/external-destinations");
          }, 2000);
        })
        .catch((err) => {
          console.log("Delete Failed", JSON.stringify(err));
        })
    }

    const getDetails = () => {
      if(!destinationData || Object.keys(destinationData).length === 0) {
        session.query("census/get-destination", {id: _id }).then((data) => {
          setHasCensusId(true);
          if(!('description' in data)) {
            data.description = "";
          }
          setDestinationData(data);
          setOriginalDestinationData(data);
          params.delete("new");
          const newSearch = params.toString();
          const newUrl = location.pathname + (newSearch ? `?${newSearch}` : "");
          window.history.replaceState({}, "", newUrl);
        })
        .catch(err => console.error('Fetch error:', err));
      }
    }

    const initialSetup = () => {
      if(!_id) { return; }

      if(!_new) {
        getDetails();
        return;
      }

      if(hasRunCompleteDestination.current) {
        return;
      }
      hasRunCompleteDestination.current = true;

      // check if it has the 'census_destination_id'
      session.command("census/complete-destination/", {"am_destination_id": _id}, {}, { defaultErrorHandling: false }).then((res) => {
        if("census_destination_id" in res) {
          // has the 'census_destination_id': call to get all the details
          getDetails();
        } else {
          // no 'census_destination_id': redirect to the 'create destination' page
          history.push("/external-destination-form");
        }
      })
      .catch((err) => {
        console.log("Failed to get destination details", err);
      }) 
    }

    useEffect(() => {
      if(_id && !loaded) {
        setLoaded(true);
        initialSetup();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if(hasCensusId && Object.keys(destinationData).length) {
        /* check if there is changes */
        let new_name = destinationData?.name;
        let new_description = destinationData?.description || "";
        let new_status = destinationData?.enabled;
        let new_companies = destinationData?.external_destination_allowed;
        let original_name = originalDestinationData?.name;
        let original_description = originalDestinationData?.description || "";
        let original_status = originalDestinationData?.enabled;
        let original_companies = originalDestinationData?.external_destination_allowed;

        if(
          (new_name !== original_name) || 
          (new_description !== original_description) ||
          (new_status !== original_status) ||
          !isEqual(new_companies, original_companies)
        ) {
          setDisableSave(false);
        } else {
          setDisableSave(true);
        }

        /* check the values are filled */
        if(!new_name.length || !new_description.length) {
          setDisableSave(true);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationData])

    
    /* wait to check if has census id */
    if(!hasCensusId) {
      return (<div className="destination-loading-styles mt-5"><LoadingDots message="loading..." /></div>);
    }

    return (
      <div className="page admin-page">
        <div className="list-header sticky-page-header">
          <button className="new-button" onClick={onClickBack}>
            <i className="bi bi-chevron-left"></i> External Destinations
          </button>
          <h1>Edit External Destination Form</h1>
          <i className="spacer" />
          <button className="new-button" onClick={onClickSave} disabled={disableSave}>
            Save Changes
          </button>
        </div>
        <div className="m-5">
          <section className="destination-edit-section">
            <div>
              <label htmlFor="name">*NAME:</label>
              <input type="text" id="name"
                value={destinationData?.name || ""}
                onChange={setName}
              />
              {!destinationData?.name?.length ? <span className="text-danger"> *REQUIRED</span> : ""}
            </div>

            <div>
              <label htmlFor="description">*DESCRIPTION:</label>
              <input type="text" id="description"
                value={destinationData?.description || ""}
                onChange={setDescription}
              />
              {!destinationData?.description?.length ? <span className="text-danger"> *REQUIRED</span> : ""}
            </div>

            <div className="enabled-status">
              <label>&nbsp;STATUS:</label>
              <span>
                <input
                  type="checkbox"
                  checked={!!destinationData?.enabled || false}
                  onChange={(e) =>
                    setDestinationData(prev => ({ ...prev, enabled: e.target.checked }))
                  }
                />
                {destinationData.enabled ? (<span> Enabled <i className="bi bi-check-circle text-success"/></span>) : 
                (<span> Disabled <i className="bi bi-dash-circle text-danger"/></span>)}
              </span>
            </div>

            <CompanySelector
              onSelectionChange={setSelectedCompanyIds}
              selectedCompanies={destinationData.external_destination_allowed}
            />

            <div className="destination-action-section">
              <fieldset className="danger-zone mt-5">
                <div className="mb-3">By selecting Delete Destination, you will <b>permanently delete</b> this Destination.<br />
                This action can not be undone.</div>
                <div className="delete-btn-wrapper">
                  <Button
                    color="danger"
                    outline
                    className="btn-red-error"
                    onClick={() => setShowDeleteModal(true)}
                  >Delete Destination</Button>
                </div>
              </fieldset>
            </div>

          </section>
        </div>
        <Modal centered backdrop isOpen={showDeleteModal}>
          <ModalHeader>
            Delete Destination
          </ModalHeader>
          <ModalBody>
            <Alert color="light" className="d-flex">
              <i className="bi bi-exclamation-octagon text-danger"></i>
              <div>
                Selecting <b>delete</b> will permanently remove this destination from AudienceMaker and cannot be undone. Are you sure you want to continue?
              </div>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button className="promote" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button onClick={onClickDelete}>Delete</Button>
          </ModalFooter>
        </Modal>
        <ConfirmSuccess show={showUpdateSuccess} label="Destination updated successfully." />
        <ConfirmSuccess show={showDeleteSuccess} label="Destination deleted successfully." />
      </div>
    )
}

/* select the sub-companies */
const CompanySelector = ({ onSelectionChange, selectedCompanies }) => {
  const session = useContext(SessionContext);
  const [companiesList, setCompaniesList] = useState([]);
  const selectedIds = selectedCompanies.map(item => item.company_id);

  const handleCheckboxChange = (id) => {
    let newSelection;
    if (selectedIds.includes(id)) {
      newSelection = selectedCompanies.filter(item => item.company_id !== id);
    } else {
      newSelection = [...selectedCompanies, { company_id: id }];
    }
    onSelectionChange(newSelection);
  };

  useEffect(() => {
    let _temp = [];
    let parent_company = session.appState.activeCompany;
    _temp.push({name: `${parent_company.name} (Current Company)`, id: parent_company.id})
    if(!companiesList.length) {
      session.query("companies/subcompanies", {}).then((res) => {
        if(res && res.length) {
          res.forEach((val) => {
            _temp.push({name: val.name, id: val.id});
          })
          setCompaniesList(_temp);
        } else {
          setCompaniesList(_temp);
        }
      })
      .catch(err => console.error('Fetch error:', err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="company-selector">
      <h6 className="">Companies Allowed to Use This External Destination:</h6>
      {companiesList.map((company) => (
        <div key={company.id}>
          <input
            type="checkbox"
            checked={selectedIds.includes(company.id)}
            onChange={() => handleCheckboxChange(company.id)}
          />
          {company.name}
        </div>
      ))}
    </div>
  );
}
